import React, { useContext, useRef, useState, useCallback } from 'react';
import {
  arrayOf,
  bool, customType,
  extend,
  params,
  QueryContext, QueryProvider,
  shape,
  string,
  useDataModel
} from '@thd-nucleus/data-sources';
import { node } from 'prop-types';
import {
  Col,
  Row,
  Placeholder
} from '@thd-olt-component-react/core-ui';
import { ExperienceContext, useStoreId, useConfigService } from '@thd-nucleus/experience-context';
import { PIPRedirector, DynamicComponent, AppContext } from '@thd-nucleus/app-render';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import {
  AugmentedReality,
  RoomVo,
  BathRenovation,
  Avataar,
  RoomVoInspire
} from '@thd-olt-component-react/augmented-reality';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Buybox } from '@thd-olt-component-react/buybox';
import { PIPCalculator } from '@thd-olt-component-react/calculator';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { EducationalSummary } from '@thd-olt-component-react/educational-summary';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import { HdhomeSignup } from '@thd-olt-component-react/email-signup';
import { declareContexts, Hydrator } from '@thd-olt-component-react/hydrator';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { MeasurementGuide } from '@thd-olt-component-react/measurement-guide';
import { MediaGalleryWithHotspotsDesktop } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { FeatureBasedRecommendationsImporter } from '@thd-olt-component-react/feature-based-recommendations';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { Promotion } from '@thd-olt-component-react/promotion';
import { PromotionProductsAnchor } from '@thd-olt-component-react/promotion-products';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';
import {
  ProductOverviewDesktop,
  ProductOverviewAccordionTitle
} from '@thd-olt-component-react/product-overview';
import {
  QuestionsAndAnswersDesktop,
  QuestionsAndAnswersAccordionTitle
} from '@thd-olt-component-react/questions-and-answers';
import { RelatedSearch, PopularVariations } from '@thd-olt-component-react/related-search';
import { ReturnsMessaging } from '@thd-olt-component-react/returns-messaging';
import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { BazaarVoiceCarousel } from '@thd-olt-component-react/social-media-container';
import { EmtLinks } from '@thd-olt-component-react/emt-links';
import { SpecificationsAccordionTitle } from '@thd-olt-component-react/specifications';
import { StickyNav } from '@thd-olt-component-react/sticky-nav';
import {
  RatingsAndReviewsDesktopImporter,
  RatingsAndReviewsAccordionTitle
} from '@thd-olt-component-react/ratings-and-reviews';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import {
  Sharebelt,
  ShareBeltPrint,
  ShareBeltShareButton,
  ShareFavorite
} from '@thd-olt-component-react/thd-sharebelt';
import {
  DynamicRecsWrapper,
  PipSemWrapper,
  RecentlyViewedWrapper,
  SupplementalRecommendations
} from '@thd-olt-component-react/thd-recs-containers';
import { SponsoredPipBanner } from '@thd-olt-component-react/sponsored-content';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { Link, Accordion, AccordionBody, AccordionHeader } from '@one-thd/sui-atomic-components';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { ProductPageEventCountdownTimer } from '@thd-olt-component-react/event-countdown-timer';
import { BundlePmvFbtLayoutGmAndHdh } from '@thd-olt-component-react/bundle-mini';
import {
  shouldDisplayPipInStock,
  shouldDisplayReferrerRecommendations,
  shouldDisplayVisuallySimilar,
  shouldDisplayPipAlternativeRecs,
  shouldDisplayIrgCollection,
  shouldDisplayPipCollections,
  shouldDisplayFbr,
  shouldDisplayIrgaccessories,
  shouldDisplayIrgcollections,
  isBathCategory,
  getProductTaxonomy
} from './product-utils';
import {
  QuestionsAndAnswersAccordionBody,
  QuestionsAndAnswersAccordionBodyLoad,
  RatingsAndReviewsAccordionBody,
  RatingsAndReviewsAccordionBodyLoad,
  ProductOverviewAccordionBody,
  ProductOverviewAccordionBodyLoad,
  SpecificationsAccordionBody,
  SpecificationsAccordionBodyLoad,
  MagicApronBotPrompt,
  MagicApronBotPromptLoad
} from './dynamic-imports';
import { pipCampaignId, requestKey, subscriptionType } from '../../constants';
import '../../styles/product-page.scss';

const ZoneCard = ({ children }) => {
  return <div className="zone-card">{children}</div>;
};

ZoneCard.propTypes = {
  children: node.isRequired
};

export const Product = () => {
  declareContexts([QueryContext, ExperienceContext, CustomerContext]);
  const theme = useTheme(Product);
  const {
    hideConsumerCardMessaging, hideReturnsMessaging, hideShareBeltShareButton, hideAddToList, hideAisleBay,
    hideYouMightAlsoNeed,
    hideSponsoredCarousel,
    hideRelatedProducts
  } = theme.props;
  const HYDRATION_STANDARD_SCROLL_BUFFER = 750;
  const STICKY_HEADER_HEIGHT = 115;
  const ctx = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const { defaultVariables } = useContext(QueryContext) || {};
  const { instance } = useContext(AppContext);
  const thdSeoBotDetected = instance?.thdSEOBotDetection;
  const { isServer, referer } = ctx;
  const viewReferer = shouldDisplayReferrerRecommendations(referer);
  const { itemId } = useParams();
  const storeId = useStoreId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  let fsRecsPipCollection = useConfigService('fs:isRecsCollectionEnable');
  let fsSupplementalRecs = useConfigService('fs:showSupplementalRecs');
  const pipRecsPositionAbove = useConfigService('fs:pipRecsPositionAbove');
  const pipRecsPositionBelow = useConfigService('fs:pipRecsPositionBelow');
  const pipRecsPositionDefault = useConfigService('fs:pipRecsPositionDefault');
  const magicApronQABotEnabled = useConfigService('fs-prop:magic-apron-qa-bot-enabled');

  const stickyNavRef = useRef(null);
  const productOverviewRef = useRef(null);
  const specificationsRef = useRef(null);
  const questionsRef = useRef(null);
  const ratingsRef = useRef(null);
  const thdCustomer = useThdCustomer();

  const onChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      navigate(canonicalUrl, { state: { disableScroll: true } });
    }
  };

  const { data: productData, loading } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: false
  });

  const pipRecsABTest = !!(
    pipRecsPositionAbove
    || pipRecsPositionBelow
    || pipRecsPositionDefault
  );

  const shouldDisplayRecsAbove = (
    pipRecsPositionAbove
    || (!pipRecsABTest && shouldDisplayReferrerRecommendations(referer))
  );

  const shouldDisplayRecsBelow = pipRecsPositionBelow;

  const shouldDisplayRecsDefault = (
    pipRecsPositionDefault
    || (!pipRecsABTest && shouldDisplayPipAlternativeRecs(productData, referer))
  );

  const [expanded, setExpanded] = useState('');

  const handleAccordionChange = useCallback((accordion, canCloseAccordion = true) => {
    setExpanded((prevState) => {
      const isExpanded = !!prevState[accordion];
      if (isExpanded && !canCloseAccordion) {
        return prevState;
      }
      return { ...prevState, [accordion]: !isExpanded };
    });
  }, []);

  const scrollTo = useCallback((offset, callback) => {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener('scroll', onScroll);
        callback();
      }
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    });
  }, []);

  const handleProductDetailsClick = useCallback((accordion) => {
    handleAccordionChange(accordion, false);
  }, [handleAccordionChange]);

  const handleViewMoreDetails = () => {
    const offset = document.getElementById('product-section-overview').offsetTop - STICKY_HEADER_HEIGHT;
    scrollTo(offset, () => handleAccordionChange('#product-section-overview', false));
  };

  const productTaxonomy = getProductTaxonomy(productData);

  return (
    <>
      <ErrorBoundary name="redirector">
        <PIPRedirector disableCanonicalRedirect disableCMMMCRedirect />
      </ErrorBoundary>
      <ErrorBoundary name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </ErrorBoundary>

      <ErrorBoundary name="pip-content">
        <Row className="isBound pip-page">
          <Row>
            <ErrorBoundary name="stickynav">
              <StickyNav
                itemId={itemId}
                ref={stickyNavRef}
                ratingsRef={ratingsRef}
                onRatingsClick={() => handleAccordionChange('#product-section-rr', false)}
              >
                <StickyNav.Links>
                  <StickyNav.Link targetRef={productOverviewRef}>
                    <div
                      className="navlink-pso"
                      onClick={() => handleAccordionChange('#product-section-overview', false)}
                      role="button"
                      tabIndex={0}
                    >
                      Product Details
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={specificationsRef}>
                    <div
                      className="navlink-specs"
                      onClick={() => handleAccordionChange('#product-section-key-feat', false)}
                      role="button"
                      tabIndex={0}
                    >
                      Specifications
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={questionsRef}>
                    <div
                      className="navlink-qa"
                      onClick={() => handleAccordionChange('#product-section-qa', false)}
                      role="button"
                      tabIndex={0}
                    >
                      Questions & Answers
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={ratingsRef}>
                    <div
                      className="navlink-rr"
                      onClick={() => handleAccordionChange('#product-section-rr', false)}
                      role="button"
                      tabIndex={0}
                    >
                      Customer Reviews
                    </div>
                  </StickyNav.Link>
                </StickyNav.Links>
              </StickyNav>
            </ErrorBoundary>
          </Row>

          <Row className="u--paddingTop u--paddingBottom">
            <ErrorBoundary name="breadcrumb-sharebelt-grp">
              <Col className="u__visible" flatten sm="7">
                <ErrorBoundary name="breadcrumbs">
                  <Breadcrumbs
                    itemId={itemId}
                  />
                </ErrorBoundary>
              </Col>
              <Col flatten sm="5">
                <ErrorBoundary name="product-info-bar">
                  <div className="product-info-bar--hide-mobile u__flex-end">
                    <ProductInfoBar itemId={itemId} />
                  </div>
                </ErrorBoundary>
              </Col>
            </ErrorBoundary>
          </Row>
          {shouldDisplayRecsAbove && (
            <ZoneCard>
              <PipSemWrapper
                schemaName="pipsem"
                anchorId={itemId}
                hideATC
                withReferrer={viewReferer}
              />
            </ZoneCard>
          )}
          <Row>
            <ErrorBoundary name="zone-a">
              <Col nopadding>
                <Row>
                  <Col>
                    <Discontinued itemId={itemId} />
                  </Col>
                  <Col className="sticky" flatten sm="7">
                    <div className="zone-card overflow">
                      <Row>
                        <Col sm="12">
                          <ErrorBoundary name="product-details">
                            <ProductDetails itemId={itemId}>
                              <ProductDetails.Badge itemId={itemId} />
                              <ProductDetails.BrandCollection showBrandHyperLink />
                              <div className="product-details__badge-title--wrapper">
                                <ProductDetails.Title />
                              </div>
                              {!hideAddToList && (
                                <div className="product-details__favorites">
                                  <Sharebelt itemId={itemId}>
                                    <ShareFavorite showCircle showFavoritesCount />
                                  </Sharebelt>
                                </div>
                              )}
                              <ProductDetails.Ratings
                                offsetselector="#sticky-nav"
                                qaselector="#product-section-qa"
                                showBadge
                                target="#product-section-rr"
                                onClick={handleProductDetailsClick}
                              />
                            </ProductDetails>
                          </ErrorBoundary>
                        </Col>

                        <Col>
                          <ErrorBoundary name="media-gallery-desktop">
                            <MediaGalleryWithHotspotsDesktop
                              highRes
                              hover
                              itemId={itemId}
                              mainImageFetchPriority="high"
                              showLoader
                            />
                          </ErrorBoundary>
                        </Col>
                        {!hideShareBeltShareButton && (
                          <Col>
                            <ErrorBoundary name="sharebelt">
                              <Sharebelt horizontal itemId={itemId}>
                                <ShareBeltShareButton email />
                                <ShareBeltPrint />
                              </Sharebelt>
                            </ErrorBoundary>
                          </Col>
                        )}
                      </Row>
                    </div>
                    <ErrorBoundary name="RoomVo">
                      <Col className="zone-card overflow ar-container">
                        <Avataar itemId={itemId} />
                        {!isServer && productData?.product?.identifiers?.roomVOEnabled
                          && (
                            <RoomVo
                              itemId={itemId}
                            />
                          )}
                      </Col>
                    </ErrorBoundary>

                    <ErrorBoundary name="augmented-reality">
                      <Col className="zone-card overflow ar-container">
                        <AugmentedReality instructions itemId={itemId} />
                      </Col>
                    </ErrorBoundary>

                  </Col>
                  <Col flatten sm="5">
                    <div className="overflow">
                      <Row>
                        <ZoneCard nopadding tag={Col}>
                          <ErrorBoundary name="event-countdown-timer">
                            <ProductPageEventCountdownTimer itemId={itemId} />
                          </ErrorBoundary>
                        </ZoneCard>
                        <Col className="zone-card overflow">
                          <ErrorBoundary name="price">
                            <Price
                              channel="desktop"
                              disableRangePricing
                              displayEachUom={false}
                              itemId={itemId}
                              large
                              clsRemediation={{
                                placeholders: true,
                                preservePlaceholders: true
                              }}
                            />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card overflow promotion__card">
                          <ErrorBoundary name="promotion">
                            <Promotion hideConditionalPromoDescription itemId={itemId} />
                          </ErrorBoundary>
                        </Col>
                        {!hideConsumerCardMessaging && (
                          <Col className="zone-card overflow consumer-credit-messaging__card">
                            <ErrorBoundary name="consumer-card-messaging">
                              <ConsumerCardMessaging
                                itemId={itemId}
                                clsRemediation={{
                                  placeholders: true,
                                  preservePlaceholders: true
                                }}
                                isFinancePromoEligible
                              />
                            </ErrorBoundary>
                          </Col>
                        )}
                        <Col className="zone-card overflow">
                          <ErrorBoundary name="promotion-products">
                            <PromotionProductsAnchor itemId={itemId} />
                          </ErrorBoundary>
                        </Col>
                        <Col className="zone-card">
                          <ErrorBoundary name="salient">
                            <SalientPoints itemId={itemId} max={3}>
                              <SalientListItem>
                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                <Link underline="always" onClick={handleViewMoreDetails}>
                                  View More Details
                                </Link>
                              </SalientListItem>
                            </SalientPoints>
                          </ErrorBoundary>
                        </Col>

                        <Row>
                          <ErrorBoundary name="measurement-guide">
                            <div className="zone-card full-bleed">
                              <MeasurementGuide />
                            </div>
                          </ErrorBoundary>
                        </Row>
                        {!hideAisleBay && (
                          <Col className="zone-card overflow">
                            <ErrorBoundary name="aisle-bay">
                              <AisleBay itemId={itemId} />
                            </ErrorBoundary>
                          </Col>
                        )}
                        <Col className="zone-card overflow">
                          <ErrorBoundary name="super-sku">
                            <SuperSku
                              itemId={itemId}
                              onChange={onChange}
                              shouldSyncAttributes
                              containerSizeHref="calculator-help-me-choose-scroll-id"
                            />
                          </ErrorBoundary>
                        </Col>

                        <Col className="zone-card">
                          <ErrorBoundary name="order-sample overflow">
                            <OrderSample itemId={itemId}>Need a closer look?</OrderSample>
                          </ErrorBoundary>
                        </Col>

                        <Col flatten>
                          <ErrorBoundary name="buybox">
                            <Buybox
                              itemId={itemId}
                              renderCalculator={(buyboxItemId, onCalculate, quantityOverride) => {
                                return (
                                  <PIPCalculator
                                    itemId={buyboxItemId}
                                    onCalculate={onCalculate}
                                    quantityOverride={quantityOverride}
                                  />
                                );
                              }}
                              sticky
                              showCards
                            />
                          </ErrorBoundary>
                        </Col>
                        {!hideReturnsMessaging && (
                          <ErrorBoundary className="zone-card overflow" name="returns-messaging">
                            <ReturnsMessaging itemId={itemId} displayTimeframe />
                          </ErrorBoundary>
                        )}
                        <Col className="zone-card overflow">
                          <ErrorBoundary name="gcc">
                            <GlobalCustomConfigurator itemId={itemId} />
                          </ErrorBoundary>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Col>
            </ErrorBoundary>
          </Row>

          {shouldDisplayRecsBelow && (
            <ZoneCard>
              <PipSemWrapper
                schemaName="pipsem"
                anchorId={itemId}
                hideATC
                withReferrer={viewReferer}
              />
            </ZoneCard>
          )}

          <ErrorBoundary name="collapsible-sections">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="collapsible-sections-accordion"
                className="grid"
                placeholder={(
                  <>
                    <div
                      ref={productOverviewRef}
                      id="product-section-overview"
                      className="accordion-placeholder"
                    >
                      <Placeholder width="100%" height="82px" color="#F5F5F5" />
                    </div>
                    <div
                      ref={specificationsRef}
                      id="product-section-key-feat"
                      className="accordion-placeholder"
                    >
                      <Placeholder width="100%" height="82px" color="#F5F5F5" />
                    </div>
                    <div
                      ref={questionsRef}
                      id="product-section-qa"
                      className="accordion-placeholder"
                    >
                      <Placeholder width="100%" height="82px" color="#F5F5F5" />
                    </div>
                    <div ref={ratingsRef} id="product-section-rr" className="accordion-placeholder">
                      <Placeholder width="100%" height="82px" color="#F5F5F5" />
                    </div>
                  </>
                )}
              >
                <BundlePmvFbtLayoutGmAndHdh itemId={itemId} />
                <div className="sui-flex sui-flex-col sui-w-full">
                  <div
                    id="product-section-overview"
                    className="product-section-overview"
                    ref={productOverviewRef}
                  >
                    <Accordion
                      key="product-details-accordion"
                      expanded={expanded['#product-section-overview'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-overview')}
                    >
                      <AccordionHeader>
                        <ProductOverviewAccordionTitle />
                      </AccordionHeader>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-product-overview"
                            className="grid"
                            waitFor={ProductOverviewAccordionBodyLoad}
                            placeholder={
                              <Placeholder width="100%" height="2000px" color="#F5F5F5" />
                            }
                          >
                            <Row>
                              <ErrorBoundary name="product-overview">
                                <ProductOverviewAccordionBody
                                  itemId={itemId}
                                  showReturnMessage
                                  revJet
                                  qctx={qctx}
                                />
                              </ErrorBoundary>
                            </Row>
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div
                    id="product-section-key-feat"
                    className="product-section-key-feat"
                    ref={specificationsRef}
                  >
                    <Accordion
                      key="specifications-accordion"
                      expanded={expanded['#product-section-key-feat'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-key-feat')}
                    >
                      <AccordionHeader>
                        <div className="sui-flex sui-flex-col">
                          <SpecificationsAccordionTitle itemId={itemId} />
                        </div>
                      </AccordionHeader>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-specifications"
                            className="grid"
                            waitFor={[SpecificationsAccordionBodyLoad]}
                            placeholder={
                              <Placeholder width="100%" height="700px" color="#F5F5F5" />
                            }
                          >
                            <SpecificationsAccordionBody
                              itemId={itemId}
                              qctx={qctx}
                              oneColumn
                              isWhiteSpecTitle
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div id="product-section-qa" className="product-section-qa" ref={questionsRef}>
                    <Accordion
                      key="questions-answers-accordion"
                      expanded={expanded['#product-section-qa'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-qa')}
                    >
                      <AccordionHeader>
                        <QuestionsAndAnswersAccordionTitle
                          noSpacing
                          itemId={itemId}
                          layoutGroup={{
                            tag: Row
                          }}
                          hydrator={{
                            className: 'grid'
                          }}
                          dynamic={{
                            pageType: 'pip'
                          }}
                        />
                      </AccordionHeader>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-questions-answers"
                            className="grid"
                            waitFor={QuestionsAndAnswersAccordionBodyLoad}
                            placeholder={
                              <Placeholder width="100%" height="700px" color="#F5F5F5" />
                            }
                          >
                            <QuestionsAndAnswersAccordionBody
                              itemId={itemId}
                              seoPageNumber={1}
                              layoutGroup={{
                                tag: Row
                              }}
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div id="product-section-rr" className="product-section-rr" ref={ratingsRef}>
                    <Accordion
                      key="ratings-reviews-accordion"
                      expanded={expanded['#product-section-rr'] || thdSeoBotDetected}
                      onChange={() => handleAccordionChange('#product-section-rr')}
                    >
                      <AccordionHeader>
                        <RatingsAndReviewsAccordionTitle
                          noSpacing
                          itemId={itemId}
                          layoutGroup={{
                            tag: Row
                          }}
                          hydrator={{
                            className: 'grid'
                          }}
                          dynamic={{
                            pageType: 'pip'
                          }}
                        />
                      </AccordionHeader>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-ratings-reviews"
                            className="grid"
                            waitFor={RatingsAndReviewsAccordionBodyLoad}
                            placeholder={
                              <Placeholder width="100%" height="2000px" color="#F5F5F5" />
                            }
                          >
                            <RatingsAndReviewsAccordionBody
                              itemId={itemId}
                              qctx={qctx}
                              prosAndCons
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                </div>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>

          {magicApronQABotEnabled && (
            <ErrorBoundary name="magic-apron-qa-bot">
              <DynamicComponent pageType="pip">
                <Hydrator
                  id="product-section-magic-apron-qa-bot"
                  className="grid"
                  scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                  waitFor={MagicApronBotPromptLoad}
                  placeholder={<Placeholder width="100%" height="276px" color="#F5F5F5" />}
                >
                  <MagicApronBotPrompt qctx={qctx} itemId={itemId} />
                </Hydrator>
              </DynamicComponent>
            </ErrorBoundary>
          )}

          <ErrorBoundary name="bv-social-media-container">
            <DynamicComponent pageType="pip">
              <div className="sui-w-full sui-my-2 empty:sui-my-0">
                <BazaarVoiceCarousel
                  itemId={itemId}
                  siteName="homedepot-gqfoln"
                  lazyLoadGallery
                />
              </div>
            </DynamicComponent>
          </ErrorBoundary>

          <ErrorBoundary name="roomvo-inspire-banner">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="product-section-roomvo-inspire-banner"
                className="grid"
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                placeholder={<Placeholder width="100%" height="150px" color="#F5F5F5" />}
              >
                <Row className="sui-pt-4 sui-pb-6">
                  <RoomVoInspire />
                </Row>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>

          <ErrorBoundary name="bath-renovation-banner">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="product-section-bath-renovation-banner"
                className="grid"
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                placeholder={<Placeholder width="100%" height="150px" color="#F5F5F5" />}
              >
                <ZoneCard className="zone-card">
                  <BathRenovation itemId={itemId} />
                </ZoneCard>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>

          {shouldDisplayPipInStock(productData, referer) && (
            <DynamicRecsWrapper
              schemaName="pipinstock"
              anchorId={itemId}
              hideATC
              requestKey={requestKey}
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'pipinstock',
                delay: 1500,
                scrollBuffer: 100
              }}
            />
          )}

          {(isBathCategory(productData) && fsRecsPipCollection && shouldDisplayPipCollections(productData)) && (
            <DynamicRecsWrapper
              schemaName="pipcollection"
              anchorId={itemId}
              requestKey={requestKey}
              isCategoryTab
              showColUpLevel
              isCollection
              hideFavorites={false}
              hideSwatches={false}
              maxResults="18"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'recs_pipcollection',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}
          <SponsoredPipBanner
            browserId={thdCustomer?.mcvisID || ''}
            pageContext={{
              label: 'pip',
              data: {
                itemId,
                treatment: 'A'
              }
            }}
            errorBoundary
            name="pip-sponsored-banner-A"
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              id: 'hydrated-sponsored-pip-banner-A'
            }}
          />
          <EducationalSummary
            itemId={itemId}
            layoutGroup={{
              tag: Row
            }}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'grid'
            }}
          />

          {shouldDisplayRecsDefault && (
            <DynamicRecsWrapper
              schemaName="pip_alternatives"
              anchorId={itemId}
              requestKey={requestKey}
              recsContext={productTaxonomy}
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: pipRecsPositionDefault ? 'RecsDefault' : 'pip_alternatives',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
              impressionData={{
                component: 'DynamicRecs',
                type: 'product'
              }}
            />
          )}

          { !hideSponsoredCarousel && (
            <ErrorBoundary name="sponsored-carousel-nucleus">
              <RetailMediaCarousel
                breadCrumbs={productData?.product?.taxonomy?.breadCrumbs}
                pageContext={{
                  schema: 'pip_sponsored',
                  data: { itemId },
                  isPip: true
                }}
                showSponsoredCarousel
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  delay: 1500,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                }}
              />
            </ErrorBoundary>
          )}

          {shouldDisplayVisuallySimilar(productData) && (
            <DynamicRecsWrapper
              schemaName="visuallysimilar_1_0"
              anchorImage={productData?.product?.media?.image?.url}
              anchorId={itemId}
              requestKey={requestKey}
              recsContext={productTaxonomy}
              errorBoundary
              checkMinimumProducts
              minimumProductCount={4}
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'visuallysimilar_1_0',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}

          {(!isBathCategory(productData) && fsRecsPipCollection && shouldDisplayPipCollections(productData)) && (
            <DynamicRecsWrapper
              schemaName="pipcollection"
              anchorId={itemId}
              requestKey={requestKey}
              isCategoryTab
              showColUpLevel
              isCollection
              hideFavorites={false}
              hideSwatches={false}
              maxResults="18"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'recs_pipcollection',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}
          {shouldDisplayIrgcollections(productData) && (
            <DynamicRecsWrapper
              schemaName="brand_based_collection"
              anchorId={itemId}
              requestKey={requestKey}
              isCategoryTab
              isIRG
              hideFavorites={false}
              maxResults="18"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'recs_brand_based_collection',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}
          {shouldDisplayIrgaccessories(productData) && !hideYouMightAlsoNeed && (
            <DynamicRecsWrapper
              schemaName="irgaccessories"
              anchorId={itemId}
              requestKey={requestKey}
              recsContext={productTaxonomy}
              isIRG
              hideFavorites={false}
              maxResults="18"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'recs_irgaccessories',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}

          {fsSupplementalRecs && (
            <SupplementalRecommendations
              schemaName="ctl"
              anchorId={itemId}
              requestKey={requestKey}
              errorBoundary
              isDrawerShow
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'ctl',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}
          <QueryProvider
            defaultVariables={defaultVariables?.current}
            persist
            cacheKey="rv-pip-recs"
          >
            <RecentlyViewedWrapper
              schemaName="rv_gm_pip_rr"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'rv_gm_pip_rr',
                scrollBuffer: 600,
                delay: 2000
              }}
            />
          </QueryProvider>
          {shouldDisplayFbr() && (
            <FeatureBasedRecommendationsImporter
              hideOnMobile
              itemId={itemId}
              qctx={qctx}
              layoutGroup={{
                tag: Row
              }}
              hydrator={{
                tag: ZoneCard,
                className: 'grid'
              }}
              dynamic={{
                pageType: 'pip'
              }}
            />
          )}

          <HdhomeSignup
            formSubmission
            subscriptionType={subscriptionType}
            campaignId={pipCampaignId}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'grid',
              tag: ZoneCard
            }}
          />
          {!hideRelatedProducts && (
            <RelatedSearch
              itemId={itemId}
              layoutGroup={{
                tag: Row
              }}
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'col__12-12'
              }}
            />
          )}

          <PopularVariations
            productId={itemId}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'col__12-12'
            }}
          />

          <EmtLinks
            path={pathname}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'col__12-12 sui-mt-4'
            }}
          />
        </Row>
      </ErrorBoundary>
    </>
  );
};

Product.displayName = 'ProductPage';

Product.propTypes = {};

Product.defaultThemeProps = {
  hideConsumerCardMessaging: false,
  hideReturnsMessaging: false,
  hideShareBeltShareButton: false,
  hideAddToList: false,
  hideAisleBay: false,
  hideYouMightAlsoNeed: false,
  hideSponsoredCarousel: false,
  hideRelatedProducts: false
};

Product.dataModel = extend(
  {
    clientOnlyProduct: params({ itemId: string().isRequired,
      loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
        svocID: string(),
        programTiers: arrayOf(shape({
          tier: string(),
          program: string()
        }))
      }) }).shape({
      itemId: string(),
      dataSources: string(),
      info: shape({
        recommendationFlags: shape({
          visualNavigation: bool(),
          packages: bool(),
          pipCollections: bool(),
          ACC: bool(),
          collections: bool(),
          frequentlyBoughtTogether: bool(),
          bundles: bool()
        }),
        returnable: string()
      }),
      identifiers: shape({
        roomVOEnabled: bool()
      }),
      availabilityType: shape({
        discontinued: bool(),
        type: string()
      }),
      fulfillment: params({ storeId: string() }).shape({
        backordered: bool(),
        fulfillmentOptions: arrayOf(
          shape({
            type: string(),
            fulfillable: bool(),
            services: arrayOf(
              shape({
                type: string(),
                locations: arrayOf(
                  shape({
                    isAnchor: bool(),
                    inventory: shape({
                      isLimitedQuantity: bool(),
                      isOutOfStock: bool()
                    })
                  })
                )
              })
            )
          })
        )
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      seoDescription: string()
    })
  },
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
      itemId: string(),
      fulfillment: params({ storeId: string() }).shape({
        anchorStoreStatusType: string(),
        backordered: bool(),
        backorderedShipDate: string(),
        bossExcludedShipStates: string(),
        bodfsAssemblyEligible: bool(),
        seasonStatusEligible: bool(),
        excludedShipStates: string(),
        fulfillmentOptions: shape({
          fulfillable: bool(),
          messages: string(),
          services: shape({
            type: string(),
            isDefault: bool(),
            deliveryCharge: string(),
            deliveryDates: shape({
              endDate: string(),
              startDate: string()
            }),
            deliveryTimeline: string(),
            freeDeliveryThreshold: string(),
            totalCharge: string(),
            locations: shape({
              type: string(),
              isAnchor: bool(),
              locationId: string(),
              storeName: string(),
              storePhone: string(),
              state: string(),
              distance: string(),
              inventory: shape({
                quantity: string(),
                isInStock: bool(),
                isOutOfStock: bool(),
                isLimitedQuantity: bool(),
                isUnavailable: bool(),
                maxAllowedBopisQty: string(),
                minAllowedBopisQty: string()
              })
            })
          }),
          type: string()
        })
      })
    })
  },
  AisleBay,
  AugmentedReality,
  BathRenovation,
  Breadcrumbs,
  Buybox,
  ConsumerCardMessaging,
  Discontinued,
  DynamicRecsWrapper,
  EmtLinks,
  ProductPageEventCountdownTimer,
  FeatureBasedRecommendationsImporter,
  GlobalCustomConfigurator,
  HdhomeSignup,
  MediaGalleryWithHotspotsDesktop,
  OrderSample,
  PIPCalculator,
  PIPRedirector,
  PipSemWrapper,
  PopularVariations,
  Price,
  PriceClearanceDataModel,
  ProductDetails,
  ProductInfoBar,
  ProductMetadata,
  ProductOverviewDesktop,
  Promotion,
  PromotionProductsAnchor,
  QuestionsAndAnswersDesktop,
  RatingsAndReviewsDesktopImporter,
  RecentlyViewedWrapper,
  RelatedSearch,
  SalientPoints,
  Sharebelt,
  StickyNav,
  SuperSku,
  BundlePmvFbtLayoutGmAndHdh
);
